import { useMemo } from "react";
import type { User } from "../../shared/generatedDrizzleSchema";
import { queries, roundedHoursAgo } from "../../shared/queries";
import { useNow } from "./useNow";
import { usePageVisible } from "./usePageVisible";
import { useQuery } from "@rocicorp/zero/react";

const presenceHeartbeatMs = 2_000;
const presenceTimeoutMs = 4_000;
type PresenceRow = {
  userId: string;
  workflowId: string;
  branchId: string;
  stepDir: string | null;
  lastSeenAt: number;
  user?: User;
};

function addToSetMap(
  map: Map<string, Set<string>>,
  key: string,
  value: string
) {
  let set = map.get(key);
  if (!set) {
    set = new Set();
    map.set(key, set);
  }
  set.add(value);
}

export function buildPresenceMaps(rows: readonly PresenceRow[], since: number) {
  const presentByWorkflow = new Map<string, Set<string>>();
  const presentByBranch = new Map<string, Set<string>>();
  const presentByStep = new Map<string, Map<string, Set<string>>>();
  const presentBranchesByUser = new Map<string, Set<string>>();
  const usersById = new Map<string, User>();

  for (const r of rows) {
    if (r.lastSeenAt < since) continue;
    addToSetMap(presentByWorkflow, r.workflowId, r.userId);
    addToSetMap(presentByBranch, r.branchId, r.userId);
    if (r.stepDir) {
      let branchSteps = presentByStep.get(r.branchId);
      if (!branchSteps) {
        branchSteps = new Map();
        presentByStep.set(r.branchId, branchSteps);
      }
      addToSetMap(branchSteps, r.stepDir, r.userId);
    }
    addToSetMap(presentBranchesByUser, r.userId, r.branchId);
    if (r.user) usersById.set(r.userId, r.user);
  }

  return {
    presentByWorkflow,
    presentByBranch,
    presentByStep,
    presentBranchesByUser,
    usersById,
  };
}

export function useGetPresence(options?: { enabled?: boolean }) {
  const { enabled = true } = options ?? {};
  const visible = usePageVisible();
  const now = useNow(presenceHeartbeatMs, enabled && visible);
  const since = now - presenceTimeoutMs;
  const [rows] = useQuery(
    enabled
      ? queries.userBranchPresence({
          since: roundedHoursAgo(1),
        })
      : undefined
  );

  return useMemo(() => buildPresenceMaps(rows ?? [], since), [rows, since]);
}
